import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { Building2, Users, KanbanSquare, Globe, Layers, Sparkles, Plus } from "lucide-react";
import api from "@/lib/api";

export default function CommandPalette({ open, setOpen }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ companies: [], contacts: [], deals: [], countries: [], sectors: [] });
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults({ companies: [], contacts: [], deals: [], countries: [], sectors: [] }); }
  }, [open]);

  useEffect(() => {
    if (!q) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} className="border-border">
      <CommandInput
        placeholder={t("common.search") + "…"}
        value={q}
        onValueChange={setQ}
        data-testid="cmdk-input"
      />
      <CommandList>
        <CommandEmpty>{t("common.none")}</CommandEmpty>

        <CommandGroup heading={t("common.actions")}>
          <CommandItem onSelect={() => go("/companies")} data-testid="cmdk-action-add-company">
            <Plus className="h-4 w-4 mr-2 text-primary" /> {t("companies.new")}
          </CommandItem>
          <CommandItem onSelect={() => go("/ai-agent")} data-testid="cmdk-action-ai">
            <Sparkles className="h-4 w-4 mr-2 text-primary" /> {t("nav.ai_agent")}
          </CommandItem>
          <CommandItem onSelect={() => go("/pipeline")} data-testid="cmdk-action-pipeline">
            <KanbanSquare className="h-4 w-4 mr-2 text-primary" /> {t("nav.pipeline")}
          </CommandItem>
        </CommandGroup>

        {results.companies.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("nav.companies")}>
              {results.companies.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/companies/${c.id}`)}>
                  <Building2 className="h-4 w-4 mr-2" /> {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.contacts.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("nav.contacts")}>
              {results.contacts.map((c) => (
                <CommandItem key={c.id} onSelect={() => go("/contacts")}>
                  <Users className="h-4 w-4 mr-2" /> {c.first_name} {c.last_name}
                  <span className="ml-auto text-xs text-muted-foreground">{c.company_name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.deals.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("nav.pipeline")}>
              {results.deals.map((d) => (
                <CommandItem key={d.id} onSelect={() => go("/pipeline")}>
                  <KanbanSquare className="h-4 w-4 mr-2" /> {d.title}
                  <span className="ml-auto text-xs text-muted-foreground tabular-nums">{(d.value||0).toLocaleString()} €</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {(results.countries.length > 0 || results.sectors.length > 0) && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("nav.reference")}>
              {results.countries.map((c) => (
                <CommandItem key={c.id} onSelect={() => go("/countries")}>
                  <Globe className="h-4 w-4 mr-2" /> {c.flag_emoji} {c.name}
                </CommandItem>
              ))}
              {results.sectors.map((s) => (
                <CommandItem key={s.id} onSelect={() => go("/sectors")}>
                  <Layers className="h-4 w-4 mr-2" /> {s.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
